import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import PageHeader from "../components/PageHeader";
import { requestWithToken } from "../utils/apis";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FormButtons from "../components/pages/FormButtons";
import SectionsTabs from "../components/pages/SectionsTabs";
import SectionsGrid from "../components/pages/SectionsGrid";
import PageGeneralDetails from "../components/pages/PageGeneralDetails";
import PageSEOFields from "../components/pages/PageSEOFields";
import addIcon from "../assets/plus-blue.svg";
import minusRed from "../assets/minus-circle-red.svg";
import { get } from "lodash";
import dotsIcon from "../assets/dots.svg";
import fileIcon from "../assets/file.svg";
import SelectTemplateOrSectionModal from "../components/SelectTemplateOrSectionModal";
import RegionSpecific from "../components/regionspecific";
import MediaPickerModal from "../components/MediaPickerModal";
import CodeSnippet from "../components/addsnippet";
import { useToast } from "../components/ToastProvider";
import { Spinner } from "react-bootstrap";
import QuillEditor from "../components/QuillEditor";
import { imageUrl, isVideo } from "../utils/helper";
import OGFields from "../components/pages/OGTagsFields";

const buildFieldSchema = (field) => {

  // Repeater → Array
  if (field.type === "repeater" && Array.isArray(field.fields)) {
    const nestedShape = {};
    field.fields.forEach((sub) => {
      nestedShape[sub.key] = buildFieldSchema(sub);
    });

    let schema = Yup.array().of(Yup.object().shape(nestedShape));

    if (field.required) {
      schema = schema.min(1, `${field.label} must have at least one item`);
    }
    return schema;
  }
  if (field.type === "text-editor") {
    let schema = Yup.string();
    if (field.required) schema = schema.required(`${field.label} is required`);
    return schema;
  }

  // Text / Textarea / File / URL → STRING
  if (
    field.type === "text" ||
    field.type === "textarea" ||
    field.type === "file" ||
    field.type === "url"
  ) {
    let schema = Yup.string();
    if (field.required) schema = schema.required(`${field.label} is required`);
    return schema;
  }
  // Text
  if (field.type === "text-editor") {
    let schema = Yup.string();
    if (field.required) schema = schema.required(`${field.label} is required`);
    return schema;
  }
  // Number
  if (field.type === "number") {
    let schema = Yup.number();
    if (field.required) schema = schema.required(`${field.label} is required`);
    return schema;
  }

  // Default fallback (string)
  let schema = Yup.string();
  if (field.required) schema = schema.required(`${field.label} is required`);
  return schema;
};



// Schema for all fields inside a section
const buildSectionFieldsSchema = (fields) => {
  const shape = {};
  console.log(fields, "fieldsschema");
  fields.forEach((field) => {
    shape[field.key] = buildFieldSchema(field);
  });
  return Yup.object().shape(shape);
};

// Schema for the sections array
const buildSectionsValidationSchema = (sections) => {
  return Yup.array().of(
    Yup.lazy((_, options) => {
      // find the current index
      const idx = options?.parent?.index || 0;
      const section = sections[idx];

      if (section && section.definition?.fields) {
        return Yup.object().shape({
          fields: buildSectionFieldsSchema(section.definition.fields),
        });
      }

      return Yup.object().shape({
        fields: Yup.object(),
      });
    })
  );
};

const AddPages = () => {
  const navigate = useNavigate();
  const { pageId, locale } = useParams();
  const location = useLocation();
  const localeFromState = location.state?.locale || null;
  const isNewLocale = location.state?.isNewLocale || false;
  const [activeAcc, setActiveAcc] = useState("");
  const [activeTab, setActiveTab] = useState("sections");
  const [loadingButton, setLoadingButton] = useState(null);
  const [sectionListData, setSectionListData] = useState([]);
  const [existingParentId, setExistingParentId] = useState(null);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [showRegionalBanner, setShowRegionalBanner] = useState(true);
  const { showToast } = useToast();
  const [pageLoading, setPageLoading] = useState(false);
  const [mediaPicker, setMediaPicker] = useState({
    open: false,
    targetField: null
  });
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState();
  // Use a React state for validation schema updated dynamically
  const [validationSchema, setValidationSchema] = useState(
    Yup.object({
      title: Yup.string().required("Page Title is required"),
      slug: Yup.string()
        .matches(
          /^[a-z0-9-_]+$/,
          "Slug can only contain lowercase letters, numbers, hyphens, and underscores"
        ),
      focus_keywords: Yup.array(),
      metaTitle: Yup.string(),
      metaDesc: Yup.string(),
      sections: Yup.array(), // Empty initially
    })
  );
  const buildCreatePayload = (values) => ({
    title: values.title,
    slug: values.slug,
    status: values.status,
    seo: {
      metaTitle: values.metaTitle,
      metaDesc: values.metaDesc,
      focus_keywords: values.focus_keywords.join(","),
    },
    code_snippet: values.codesnippet ? { head_tag: values.codesnippet } : undefined,
    sections: values.sections.map((sec, idx) => ({
      type: sec?.definition?.type,
      section_type_id: sec?.definition?._id,    // REQUIRED only for create
      order: idx + 1,
      data: sec.fields || {},
    })),
    og_tags:values.og_tags
  });
  const buildUpdatePayload = (values, existingParentId, selectedLocale) => ({
    title: values.title,
    slug: values.slug,
    status: values.status,
    parent_id: existingParentId,
    locale: selectedLocale,
    og_tags:values.og_tags,

    seo: {
      metaTitle: values.metaTitle,
      metaDesc: values.metaDesc,
      focus_keywords:
        Array.isArray(values.focus_keywords)
          ? values.focus_keywords.join(",")
          : values.focus_keywords || "",
    },

    code_snippet: values.codesnippet
      ? { head_tag: values.codesnippet }
      : undefined,

    sections: values.sections.map((sec, idx) => {
      const order = idx + 1;

      // ✅ EXISTING PAGE SECTION (coming from API)
      if (sec.fromPage || sec._id) {
        return {
          _id: sec._id || sec.id,      // use existing page-section id
          order,
          data: sec.fields || {},
        };
      }

      // ✅ NEWLY ADDED SECTION (during update)
      return {
        type: sec.definition?.type,
        section_type_id: sec.definition?._id,  // ONLY here
        order,
        data: sec.fields || {},
      };
    }),
  });


  const formik = useFormik({
    initialValues: {
      status: "draft",
      title: "",
      slug: "",
      focus_keywords: [],
      metaTitle: "",
      metaDesc: "",
      sections: [], 
      og_tags: {
        title: "",
        desc: "",
        url: "",
        image:""
      }
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        values.og_tags.image = values?.og_tags?.image
        ? values?.og_tags?.image.replace(process.env.REACT_APP_BUCKET_URL, "")
        : null;
        let payload = pageId
          ? buildUpdatePayload(values, existingParentId, selectedLocale)
          : buildCreatePayload(values);
        const response = pageId
          ? await requestWithToken("PUT", `/api/page/${pageId}`, payload)
          : await requestWithToken("POST", "/api/page", payload);

        if (response?.status === 200) {
          showToast("Page saved successfully.", "success");
          navigate("/pages");
        }
      } catch (error) {
        showToast(error?.response?.data?.message || "Failed to save page", "error");
      } finally {
        setSubmitting(false);
        setLoadingButton(null);
      }
    }

    // onSubmit: async (values, { setSubmitting, resetForm }) => {
    //   try {
    //     const payload = {
    //       title: values.title,
    //       slug: values.slug,
    //       status: values.status,
    //       seo: {
    //         metaTitle: values.metaTitle,
    //         metaDesc: values.metaDesc,
    //         focus_keywords: values.focus_keywords.join(","),
    //       },
    //       code_snippet: values.codesnippet
    //         ? { head_tag: values.codesnippet }
    //         : undefined,
    //       sections: values.sections.map((sec, idx) => ({
    //         type: sec?.definition?.type,
    //         section_type_id: sec?.definition?._id,
    //         order: idx + 1,
    //         data: sec.fields || {},
    //         ...(pageId ? { _id: sec._id } : {}), // only for update
    //       })),
    //       ...(pageId ? { parent_id: existingParentId, locale: selectedLocale } : {}), // only in update
    //     };
    //     let response;
    //     if (pageId) {
    //       response = await requestWithToken(
    //         "PUT",
    //         `/api/page/${pageId}`,
    //         payload
    //       );
    //     } else {
    //       response = await requestWithToken("POST", "/api/page", payload);
    //     }

    //     if (response?.status === 200) {
    //       showToast("Page saved successfully.", "success");
    //       resetForm();
    //       navigate("/pages");
    //     }
    //   } catch (error) {
    //     showToast(error?.response?.data?.message || "Failed to save page", "error");
    //     if (loadingButton === "toggle") {
    //       formik.setFieldValue("published", !formik.values.published);
    //     }
    //     console.error("Error saving page:", error);
    //   } finally {
    //     setSubmitting(false);
    //     setLoadingButton(null);
    //   }
    // },
  });

  // Update validation schema whenever sections array changes to reflect dynamic validation
  useEffect(() => {
    const schema = Yup.object({
      title: Yup.string().required("Page Title is required"),
      slug: Yup.string().matches(/^[a-z0-9-_]+$/, "Slug can only contain lowercase letters, numbers, hyphens, and underscores"),
      focus_keywords: Yup.array(),
      metaTitle: Yup.string(),
      metaDesc: Yup.string(),
      sections: buildSectionsValidationSchema(pageData?.section || []),
    });

    setValidationSchema(schema);
  }, [pageData]);   // rebuild only when page data / section definition changes


  // Fetch data in edit mode
  useEffect(() => {
    if (pageId) {
      (async () => {
        try {
          setPageLoading(true);
          // 1️⃣ FETCH section type definitions FIRST
          const sectionTypesRes = await requestWithToken(
            "GET",
            "/api/sectionType/list?page=1&limit=200"
          );

          const sectionTypes = sectionTypesRes?.data?.data?.list || [];
          setSectionListData(sectionTypes);

          // 2️⃣ NOW fetch page details
          const response = await requestWithToken(
            "GET",
            `/api/page/details/${pageId}`
          );

          if (response.status === 200) {
            const page = response.data.data;
            setPageData(page)
            setExistingParentId(page.parent_id || null);
            setSelectedLocale(page.locale || localeFromState || null);
            formik.setValues({
              title: page.title || "",
              slug: page.slug || "",
              focus_keywords: page.seo?.focus_keywords
                ? page.seo.focus_keywords.split(",")
                : [],
              metaTitle: page.seo?.metaTitle || "",
              metaDesc: page.seo?.metaDesc || "",
              status: page?.status || "draft",
              sections: page.section?.map((sec) => {
                const definition = sectionTypes.find((s) => s.type === sec.type);
                return {
                  id: sec._id,
                  _id: sec._id, 
                  fromPage: true,   
                  label: definition?.label || sec.type,
                  definition: definition || {},
                  fields: sec.data || {}
                };
              }) || [],
              og_tags:page?.og_tags || {}
            });
          }
          setPageLoading(false);
        } catch (e) {
          showToast("Failed to fetch page details", "error");
        }
      })();
    }
  }, [pageId]);

  //   useEffect(() => {
  //   if (!isNewLocale && pageId) {
  //     (async () => {
  //       try {
  //         // 1️⃣ FETCH Section Type Definitions First
  //         const sectionTypesRes = await requestWithToken(
  //           "GET",
  //           "/api/sectionType/list?page=1&limit=200"
  //         );

  //         const sectionTypes = sectionTypesRes?.data?.data?.list || [];
  //         setSectionListData(sectionTypes);

  //         // 2️⃣ FETCH Page Details
  //         const response = await requestWithToken(
  //           "GET",
  //           `/api/page/details/${pageId}?locale=${selectedLocale}`
  //         );

  //         if (response.status === 200) {
  //           const page = response.data.data;
  //           setPageData(page);

  //           formik.setValues({
  //             title: page.title || "",
  //             slug: page.slug || "",
  //             focus_keywords: page.seo?.focus_keywords
  //               ? page.seo.focus_keywords.split(",")
  //               : [],
  //             metaTitle: page.seo?.metaTitle || "",
  //             metaDesc: page.seo?.metaDesc || "",
  //             published: page?.published || false,

  //             sections:
  //               page.section?.map((sec) => {
  //                 const definition = sectionTypes.find(
  //                   (s) => s.type === sec.type
  //                 );

  //                 return {
  //                   id: sec._id,
  //                   label: definition?.label || sec.type,
  //                   definition: definition || {},
  //                   fields: sec.data || {},
  //                 };
  //               }) || [],
  //           });
  //         }
  //       } catch (e) {
  //         console.log(e);
  //         showToast("Failed to fetch page details", "error");
  //       }
  //     })();
  //   }
  // }, [pageId, selectedLocale]);


  const handleAddSection = (section, sourceType) => {
    const tab = sourceType || activeTab;

    let newSection;

    if (tab === "sections") {
      // Adding from SECTIONS tab (ready content already exists)
      newSection = {
        id: section._id,   // template/section reference id
        _id: null,         // do NOT treat as existing page section
        fromPage: false,
        type: section.type,
        label: section?.section_type_id?.label || section.type,
        fields: section.data || {},                // prefilled fields from template
        definition: section.section_type_id,       // reference schema for form UI
      };
    } else {
      // ADDING FROM templates tab (no prefilled data)
      newSection = {
        id: section._id,
        _id: null,
        fromPage: false,
        type: section.type,
        label: section.label,
        fields: {},                                 // start empty
        definition: section,
      };
    }

    const currentSections = formik.values.sections || [];
    formik.setFieldValue("sections", [...currentSections, newSection]);
    setActiveAcc(currentSections.length);
  };


  const handleRemoveSection = (idx) => {
    const updated = [...formik.values.sections];
    updated.splice(idx, 1);
    formik.setFieldValue("sections", updated);
  };

  const handleSaveTemplate = async (section) => {
    try {
      const payload = {
        type: section?.definition?.type || "",
        section_type_id: section?.definition?._id || "",
        is_template: true,
        data: section.fields || {},
      };
      const response = await requestWithToken("POST", "/api/section", payload);
      if (response?.status === 200) {
        showToast("Section saved as template successfully.", "success");
        // close dropdown menu after sving as template
        const dropdowns = document.querySelectorAll(".dropdown-menu.show");
        dropdowns.forEach((dd) => dd.classList.remove("show"));
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to save section as template", "error");
      console.error("Error saving section as template:", error);
    }
  };
  const renderFields = (
    fields,
    itemIdx,
    sectionIdx,
    prefix = `sections[${sectionIdx}].fields`
  ) =>
    fields.map((field) => {
      const fieldName = `${prefix}.${field.key}`;
      // --- TEXT EDITOR (Quill using useQuill) ---
    
      if (field.type === "text-editor") {
        return (
          <div className="frm-group" key={field.key}>
            <label className="frm-label">{field.label}</label>

            <QuillEditor
              value={get(formik.values, fieldName) || ""}
              onChange={(val) => formik.setFieldValue(fieldName, val)}
            />

            {formik.touched[fieldName] && formik.errors[fieldName] && (
              <div className="input-error">{formik.errors[fieldName]}</div>
            )}
          </div>
        );
      }


      if (field.type === "repeater" && Array.isArray(field.fields)) {
        const repeaterList = get(formik.values, fieldName, []);

        return (
          <div key={field.key} className="frm-group">
            <label className="frm-label">
              {field.label} {field.required ? "*" : ""}
            </label>

            {repeaterList.map((item, subIdx) => (
              <div
                key={subIdx}
                className="repeater-item mb-3 p-3 border rounded"
              >
                {/* 👇 recursion handles nested repeaters too */}
                {renderFields(
                  field.fields,
                  subIdx,
                  sectionIdx,
                  `${fieldName}[${subIdx}]`
                )}

                <div className="text-end">
                  <button
                    type="button"
                    className="remove-red-link mt-2 text-danger"
                    onClick={() => {
                      const updatedList = [...repeaterList];
                      updatedList.splice(subIdx, 1);
                      formik.setFieldValue(fieldName, updatedList);
                    }}
                  >
                    {/* <img src={minusRed} alt="remove" /> */}
                    Remove
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="add-blue-link"
              onClick={() => {
                const currentList = repeaterList || [];
                const newList = [...currentList, {}];
                formik.setFieldValue(fieldName, newList);
              }}
            >
              <img src={addIcon} alt="add" />
              Add {field.label}
            </button>
          </div>
        );
      }
      // media preview
      const key = field.key?.toLowerCase() || "";
      const type = field.type?.toLowerCase() || "";

      const isMediaField =
        key === "media" ||
        key === "image" ||
        type === "file" ||      // ⭐ NEW: file type
        type === "media" ||     // safe fallback
        type === "image";       // safe fallback


      // --- MEDIA FIELD WITH CLICKABLE PREVIEW ---
      if (isMediaField) {
        const fileUrl = get(formik.values, fieldName);
          const fullUrl = fileUrl
    ? `${process.env.REACT_APP_BUCKET_URL}${fileUrl}`
    : null;
        return (
          <div className="frm-group" key={field.key}>
            <label className="frm-label">{field.label}</label>

            <div
              className="pointer"
              onClick={() =>
                setMediaPicker({
                  open: true,
                  targetField: fieldName, // ← store exact field path
                })
              }
            >
              {fileUrl ? (
                isVideo(fileUrl) ? (
                  <video
                    src={fullUrl}
                    width="130"
                    height="130"
                    style={{
                      borderRadius: "12px",
                      objectFit: "cover",
                      border: "2px solid #ddd",
                    }}
                    muted
                    controls={false}
                  />
                ) : (
                  <img
                    src={fullUrl}
                    alt="preview"
                    style={{
                      width: "130px",
                      height: "130px",
                      borderRadius: "12px",
                      objectFit: "cover",
                      border: "2px solid #ddd",
                    }}
                  />
                )
              ) : (
                <div
                  style={{
                    width: "130px",
                    height: "130px",
                    borderRadius: "12px",
                    background: "#f5f5f5",
                    color: "#777",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  No Media
                </div>
              )}
            </div>
          </div>
        );
      }

      // ✅ non-repeater fields
      const error = get(formik.errors, fieldName);
      const touched = formik.touched?.sections || false;

      return (
        <div className="frm-group" key={field.key}>
          <label className="frm-label">
            {field.label} {field.required ? "*" : ""}
          </label>

          {field.type === "textarea" ? (
            <textarea
              name={fieldName}
              className="frm-input"
              placeholder={field.placeholder || ""}
              value={get(formik.values, fieldName) || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          ) : (
            <input
              type={field.type === "number" ? "number" : "text"}
              name={fieldName}
              className="frm-input"
              placeholder={field.placeholder || ""}
              value={get(formik.values, fieldName) || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          )}

          {touched && error && <div className="input-error">{error}</div>}
        </div>
      );
    });

  const renderSectionsFields = () =>
    formik.values.sections.map((section, idx) => (
      <div
        key={section.id}
        className={`theme-card p-3 mb-3 acccordian-item ${activeAcc === idx ? "active" : ""
          }`}
      >
        <div
          className="d-flex justify-content-between align-items-center accordian-header"
          onClick={() => setActiveAcc(activeAcc === idx ? "" : idx)}
        >
          <h5 className="secttl1">{section.label}</h5>
          {/* <button
            type="button"
            className="text-danger"
            onClick={() => handleRemoveSection(idx)}
          > 
            Remove
          </button> */}
          <div className="sel-box ms-auto flex-shrink-0">
            <div className="dropdown">
              <button
                type="button"
                data-bs-toggle="dropdown"
                onClick={(e) => e.stopPropagation()}
              >
                <img src={dotsIcon} alt="" />
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <div
                    className="dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveTemplate(section);
                    }}
                  >
                    <img src={fileIcon} alt="Delete" /> Save as Section
                  </div>
                </li>
                <li>
                  <div
                    className="dropdown-item text-danger"
                    onClick={() => handleRemoveSection(idx)}
                  >
                    <img src={minusRed} alt="Delete" /> Remove
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="cccordian-content-body acccordian-content">
          {renderFields(section?.definition?.fields || [], 0, idx)}
        </div>
      </div>
    ));

  const handleSaveDraft = async (l) => {

    formik.setFieldValue("status", "draft");
    setLoadingButton(l || "save");
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      setLoadingButton(null);
    }
  };

  const handlePublish = () => {
    formik.setFieldValue("status", "published");
    handleSaveDraft("toggle");
  };

  const isChildPage = !!locale;   // new detection
  const isCreateMode = !pageId;

  
  const handleWhiteAction = async () => {
    // White button: Save Draft / Update / Unpublish
    // → final status should always be "draft"
    await formik.setFieldValue("status", "draft");

    setLoadingButton("white");

    await formik.submitForm();
    setLoadingButton(null);
  };

  const handleBlackAction = async () => {
    // Black button: Publish / Update (published)
    // → final status should always be "published"
    await formik.setFieldValue("status", "published");

    setLoadingButton("black");

    await formik.submitForm();
    setLoadingButton(null);
  };




  return (
    <>
      <PageHeader pageTitle={pageId ? "Page Details" : "Add New Page"}>
        <FormButtons
          detailId={pageId || ""}
          status={formik.values.status}
          isChildPage={isChildPage}
          loadingButton={loadingButton}
          onWhiteClick={handleWhiteAction}
          onBlackClick={handleBlackAction}
          isSubmitting={formik.isSubmitting}
        />
      </PageHeader>

      <div className="page-container">
        <div className="row">
          <div className="col-md-8">
            <div className="theme-card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center ">
                <div className="card-title">Add Template or Section</div>
                <button className="btn-black" onClick={() => setShowSelectModal(true)}>Add</button>
              </div>
            </div>
            {localeFromState && showRegionalBanner && (
              <div className="regional-content-edit">
                <div>
                  You’re editing content for <span>{localeFromState}</span>
                </div>

                <button
                  className="yellow-btn"
                  onClick={() => setShowRegionalBanner(false)}
                >
                  Understood
                </button>
              </div>
            )}

            {/* <SectionsTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            <p className="sevttl1">Section: Add a new block from scratch.</p>
            <SectionsGrid
              activeTab={activeTab}
              onSelectItem={handleAddSection}
              setSectionListData={setSectionListData}
            /> */}
            {pageLoading ? (
              <div className="theme-card p-5 text-center mt-3 d_flex">
                <div className="text-center py-4">  <Spinner size="lg" /></div>
              </div>
            ) : formik.values.sections.length === 0 ? (
              <div className="theme-card p-5 text-center mt-3">
                <div className="empty-state-title mb-2">No Sections or templates Added</div>
              </div>
            ) : (
              renderSectionsFields()
            )}


          </div>
          <div className="col-md-4">
            <div className="sticky-sec">
              <PageGeneralDetails
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
              />
              {pageData && (
                <RegionSpecific pageId={pageId} pageData={pageData} setPageData={setPageData} />
              )}

              <PageSEOFields
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                setFieldValue={formik.setFieldValue}
              />
              <CodeSnippet
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
              />
               <OGFields
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                setFieldValue={formik.setFieldValue}
                showMediaPicker={showMediaPicker}
                setShowMediaPicker={setShowMediaPicker}
              />
            </div>
          </div>
        </div>
      </div>
      <SelectTemplateOrSectionModal
        show={showSelectModal}
        onClose={() => setShowSelectModal(false)}
        onInsert={(item, sourceType) => handleAddSection(item, sourceType)}
      />
      <MediaPickerModal
        show={mediaPicker.open}
        onClose={() => setMediaPicker({ open: false, targetField: null })}
        onSelect={(url) => {
          if (mediaPicker.targetField) {
            // only save the relative URL (after bucket)
            const cleanUrl = url.replace(process.env.REACT_APP_BUCKET_URL, "");

            formik.setFieldValue(mediaPicker.targetField, cleanUrl);
          }
          setMediaPicker({ open: false, targetField: null });
        }}
      />

    </>
  );
};

export default AddPages;
